import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  FileText,
  GraduationCap,
  Newspaper,
  Briefcase,
  FlaskConical,
  Loader2,
  Check,
  CheckCircle2,
  AudioWaveform,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
  useVerticals,
  useRoles,
  useRole,
  useUploadVideo,
  useJobs,
  useUsage,
  usePromptReports,
  PROMPT_REPORT_LABELS,
  type RoleListItem,
  type ScenarioDetail,
  type BriefField,
  type UploadVideoArgs,
} from "@/lib/api";
import { getStepProgress } from "@/lib/processingSteps";
import { FunnyLoader } from "@/components/FunnyLoader";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { setProjectMeta } from "@/lib/projectMeta";
import {
  DynamicBriefFields,
  initFieldValues,
  isFieldEmpty,
  buildBriefData,
  type FieldValue,
} from "@/components/DynamicBriefFields";

const ORIGINAL_LANG = "original";
const LANGUAGES = [
  { value: ORIGINAL_LANG, label: "Original" },
  { value: "en", label: "English" },
  { value: "ru", label: "Russian" },
  { value: "es", label: "Spanish" },
  { value: "de", label: "German" },
  { value: "fr", label: "French" },
  { value: "pt", label: "Portuguese" },
  { value: "zh", label: "Chinese" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "ar", label: "Arabic" },
];

const VERTICAL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  education: GraduationCap,
  media: Newspaper,
  business: Briefcase,
  science: FlaskConical,
};

function iconForVertical(slug: string) {
  return VERTICAL_ICONS[slug] ?? FileText;
}

function truncate(text: string, max = 90): string {
  const t = (text ?? "").trim();
  return t.length > max ? t.slice(0, max).trimEnd() + "…" : t;
}

function flattenScenarios(
  categories: { intents: { scenarios: ScenarioDetail[] }[] }[],
): ScenarioDetail[] {
  return categories.flatMap((c) => c.intents.flatMap((i) => i.scenarios));
}

const filenameToTitle = (name: string) =>
  name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " ").trim();

// --- File drop zone -------------------------------------------------------

function FileDropZone({
  file,
  onSelect,
  onClear,
}: {
  file: File | null;
  onSelect: (f: File) => void;
  onClear: () => void;
}) {
  const [dragOver, setDragOver] = useState(false);

  if (file) {
    return (
      <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
        <Upload className="w-5 h-5 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{file.name}</p>
          <p className="text-xs text-muted-foreground">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-muted-foreground hover:text-foreground shrink-0"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files[0];
        if (f) onSelect(f);
      }}
      className={
        "border-2 border-dashed rounded-xl p-10 text-center transition-colors " +
        (dragOver ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/40")
      }
    >
      <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
      <p className="text-sm font-medium mb-1">Drag &amp; drop your recording</p>
      <p className="text-xs text-muted-foreground mb-3">video or audio · up to 2 GB</p>
      <label>
        <input
          type="file"
          accept="video/*,audio/*"
          className="hidden"
          data-qa="new-project-file-input"
          onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            if (f) onSelect(f);
          }}
        />
        <Button variant="outline" asChild className="cursor-pointer">
          <span>Select file</span>
        </Button>
      </label>
    </div>
  );
}

// --- Processing stages ----------------------------------------------------

type StageState = "done" | "active" | "pending";

function StageRow({
  label,
  state,
  detail,
}: {
  label: string;
  state: StageState;
  detail?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="w-5 h-5 flex items-center justify-center shrink-0">
          {state === "done" ? (
            <Check className="w-4 h-4 text-emerald-600" />
          ) : state === "active" ? (
            <Loader2 className="w-4 h-4 animate-spin text-foreground" />
          ) : (
            <span className="w-2.5 h-2.5 rounded-full border-2 border-muted-foreground/30" />
          )}
        </span>
        <span
          className={
            "text-sm truncate " +
            (state === "active"
              ? "text-foreground font-medium"
              : state === "done"
                ? "text-foreground"
                : "text-muted-foreground")
          }
        >
          {label}
        </span>
      </div>
      <span
        className={
          "text-xs shrink-0 tabular-nums " +
          (state === "done"
            ? "text-emerald-600 font-medium"
            : state === "active"
              ? "text-foreground"
              : "text-muted-foreground/70")
        }
      >
        {state === "done" ? "Done" : detail ?? (state === "active" ? "Working…" : "Queued")}
      </span>
    </div>
  );
}

// --- Role card ------------------------------------------------------------

function RoleCard({
  role,
  selected,
  onSelect,
}: {
  role: RoleListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const Icon = iconForVertical(role.vertical_slug);
  const blurb = truncate(role.description || role.role_context || "");
  return (
    <Card
      role="button"
      tabIndex={0}
      data-qa={`new-project-role-${role.slug}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={
        "cursor-pointer p-4 transition-colors outline-none " +
        (selected ? "border-foreground ring-1 ring-foreground" : "hover:bg-muted/50")
      }
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-foreground" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{role.name}</p>
          {blurb ? <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{blurb}</p> : null}
        </div>
      </div>
    </Card>
  );
}

// --- Main page ------------------------------------------------------------

export function NewProjectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Optional ?role=<slug> preselection (quick-start / legacy /brief redirect).
  // Role slugs are vertical-prefixed (e.g. "media-journalist") so the prefix is
  // the vertical slug.
  const initialRole = searchParams.get("role");

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [file, setFile] = useState<File | null>(null);

  // Step 1 selection: selectedVertical === null means "Transcript only".
  const [selectedVertical, setSelectedVertical] = useState<string | null>(
    initialRole ? initialRole.split("-")[0] ?? null : null,
  );
  const [roleSlug, setRoleSlug] = useState<string | null>(initialRole || null);

  // Step 2 brief.
  const [scenarioId, setScenarioId] = useState<number | null>(null);
  const [language, setLanguage] = useState(ORIGINAL_LANG);
  const [context, setContext] = useState("");
  const [keyTerms, setKeyTerms] = useState("");
  const [fieldValues, setFieldValues] = useState<Record<string, FieldValue>>({});

  // Step 3 processing.
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadDone, setUploadDone] = useState(false);
  const [position, setPosition] = useState<number | null>(null);
  const [jobId, setJobId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const { data: usage } = useUsage();
  const { data: verticals } = useVerticals();
  const { data: roles, isLoading: rolesLoading } = useRoles(selectedVertical ?? undefined);
  const { data: role } = useRole(roleSlug ?? undefined);
  const { data: jobs } = useJobs();
  const { mutate, isPending } = useUploadVideo(setUploadProgress);

  const isTranscript = selectedVertical === null;

  // Billing gate: block proceeding when the plan's minute allowance is spent.
  const overLimit = !!usage && usage.remaining <= 0;
  const guardLimit = (proceed: () => void) => {
    if (overLimit) setUpgradeOpen(true);
    else proceed();
  };

  const scenarios = useMemo(() => (role ? flattenScenarios(role.categories) : []), [role]);
  const selectedScenario = useMemo(
    () => scenarios.find((s) => s.id === scenarioId) ?? null,
    [scenarios, scenarioId],
  );
  const briefFields = useMemo<BriefField[]>(
    () => selectedScenario?.brief_schema ?? [],
    [selectedScenario],
  );

  // Clear the scenario whenever the role changes, so a scenario id picked for a
  // previously selected role never rides along under a different role_slug — the
  // effect below re-defaults it against *this* role's own scenario list.
  useEffect(() => {
    setScenarioId(null);
  }, [roleSlug]);

  // Default to the first P0 scenario once the role loads.
  useEffect(() => {
    if (scenarioId === null && scenarios.length > 0) {
      const fallback = scenarios.find((s) => s.priority === "P0") ?? scenarios[0];
      if (fallback) setScenarioId(fallback.id);
    }
  }, [scenarios, scenarioId]);

  // Reset dynamic field values when the scenario changes.
  useEffect(() => {
    setFieldValues(initFieldValues(selectedScenario?.brief_schema ?? []));
  }, [selectedScenario]);

  // The job we just created, fetched from the polling jobs list.
  const job = useMemo(
    () => (jobId != null ? (jobs ?? []).find((j) => j.id === jobId) : undefined),
    [jobs, jobId],
  );

  // Per-report generation progress (populates once the pipeline creates the reports).
  const { data: promptReports } = usePromptReports(
    jobId != null ? String(jobId) : undefined,
    job?.status,
  );
  const totalReports = promptReports?.length ?? 0;
  const readyReports = promptReports?.filter((r) => r.status === "PROCESSED").length ?? 0;
  const failedReports = promptReports?.filter((r) => r.status === "FAILED").length ?? 0;
  const reportProgress =
    totalReports > 0 ? Math.round(((readyReports + failedReports) / totalReports) * 100) : 0;
  const allReportsDone = totalReports > 0 && readyReports + failedReports >= totalReports;

  // Open the report automatically once it's actually ready: the job is complete
  // and either there are no tracked reports or they have all finished generating.
  useEffect(() => {
    if (
      step === 3 &&
      position != null &&
      job?.status === "COMPLETED" &&
      (totalReports === 0 || allReportsDone)
    ) {
      navigate(`/reports/${position}`, { replace: true });
    }
  }, [step, job, position, totalReports, allReportsDone, navigate]);

  const selectFile = (f: File) => {
    setError(null);
    if (f.size > 2048 * 1024 * 1024) {
      setError("File too large. Maximum size is 2 GB.");
      return;
    }
    setFile(f);
  };

  const pickVertical = (slug: string) => {
    setSelectedVertical(slug);
    setRoleSlug(null);
  };

  const pickTranscript = () => {
    setSelectedVertical(null);
    setRoleSlug(null);
  };

  const missingRequired = briefFields.some((f) => f.required && isFieldEmpty(f, fieldValues[f.key]));

  const startUpload = () => {
    if (!file) return;
    setError(null);
    setUploadProgress(0);
    setUploadDone(false);

    const trimmedTerms = keyTerms.trim();
    const title = filenameToTitle(file.name);

    let args: UploadVideoArgs;
    if (isTranscript) {
      args = { file, persona: "transcript" };
    } else {
      const lang = language === ORIGINAL_LANG ? "" : language;
      const briefData: Record<string, unknown> = buildBriefData(briefFields, fieldValues);
      if (context.trim()) briefData.context = context.trim();
      if (trimmedTerms) briefData.key_terms = trimmedTerms;
      if (lang) briefData.language = lang;
      args = {
        file,
        roleSlug: roleSlug ?? undefined,
        scenarioId,
        keyTerms: trimmedTerms || undefined,
        briefData,
      };
    }

    setStep(3);
    mutate(args, {
      onSuccess: (raw: unknown) => {
        setUploadDone(true);
        const data = (raw ?? {}) as { id?: number; position?: number };
        if (data.id != null) {
          setJobId(data.id);
          setProjectMeta(data.id, {
            customTitle: title,
            keyTerms: trimmedTerms || undefined,
            titleEditedByUser: true,
          });
        }
        if (data.position != null) setPosition(data.position);
        else if (data.id != null) setPosition(data.id);
      },
      onError: (err: Error) => {
        // 402 from the upload gate surfaces as a "limit_reached" message.
        if (err.message.includes("limit_reached")) {
          setStep(1);
          setUpgradeOpen(true);
          return;
        }
        setError(err.message);
      },
    });
  };

  const headerVerticalName = role?.vertical ?? "";

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="w-full max-w-3xl mx-auto space-y-6 py-8">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl font-semibold tracking-tight">New Project</h1>
          {step !== 3 ? (
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4" />
              Cancel
            </Button>
          ) : null}
        </div>

        {/* STEP 1 — file + role selection */}
        {step === 1 ? (
          <div className="space-y-8">
            <FileDropZone file={file} onSelect={selectFile} onClear={() => setFile(null)} />

            {error ? (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            ) : null}

            <section className="space-y-3">
              <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Analysis type</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={pickTranscript}
                  className={
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors " +
                    (isTranscript
                      ? "border-foreground bg-foreground text-background"
                      : "border-border text-muted-foreground hover:bg-muted/60")
                  }
                >
                  <FileText className="w-3.5 h-3.5" />
                  Transcript only
                </button>
                {(verticals ?? []).map((v) => {
                  const Icon = iconForVertical(v.slug);
                  const active = selectedVertical === v.slug;
                  return (
                    <button
                      key={v.slug}
                      type="button"
                      data-qa={`new-project-vertical-${v.slug}`}
                      onClick={() => pickVertical(v.slug)}
                      className={
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors " +
                        (active
                          ? "border-foreground bg-foreground text-background"
                          : "border-border text-muted-foreground hover:bg-muted/60")
                      }
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {v.name}
                    </button>
                  );
                })}
              </div>
            </section>

            {!isTranscript ? (
              <section className="space-y-3">
                <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Select role</h2>
                {rolesLoading ? (
                  <p className="text-sm text-muted-foreground">Loading roles…</p>
                ) : (roles ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No roles in this category.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(roles ?? []).map((r) => (
                      <RoleCard
                        key={r.id}
                        role={r}
                        selected={roleSlug === r.slug}
                        onSelect={() => setRoleSlug(r.slug)}
                      />
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            <div className="flex justify-end pt-2">
              {isTranscript ? (
                <Button className="gap-2" disabled={!file} onClick={() => guardLimit(startUpload)}>
                  <Upload className="w-4 h-4" />
                  Upload &amp; process
                </Button>
              ) : (
                <Button
                  className="gap-2"
                  disabled={!file || !roleSlug}
                  data-qa="new-project-next"
                  onClick={() => guardLimit(() => setStep(2))}
                >
                  Next: settings
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ) : null}

        {/* STEP 2 — brief settings */}
        {step === 2 && role ? (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                {(() => {
                  const Icon = iconForVertical(role.vertical_slug);
                  return <Icon className="w-5 h-5 text-foreground" />;
                })()}
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight">{role.name}</h2>
                <p className="text-sm text-muted-foreground">{headerVerticalName}</p>
              </div>
            </div>

            {file ? (
              <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm truncate">{file.name}</span>
                <span className="text-xs text-muted-foreground ml-auto shrink-0">
                  {(file.size / (1024 * 1024)).toFixed(1)} MB
                </span>
              </div>
            ) : null}

            <section className="space-y-2">
              <label className="text-sm font-medium">Scenario</label>
              <Select
                value={scenarioId != null ? String(scenarioId) : undefined}
                onValueChange={(v) => setScenarioId(Number(v))}
              >
                <SelectTrigger className="w-full" data-qa="new-project-scenario-select">
                  <SelectValue placeholder="Select scenario" />
                </SelectTrigger>
                <SelectContent>
                  {scenarios.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedScenario?.description ? (
                <p className="text-xs text-muted-foreground">{selectedScenario.description}</p>
              ) : null}
            </section>

            <section className="space-y-2">
              <label className="text-sm font-medium">Output language</label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </section>

            <section className="space-y-2">
              <label className="text-sm font-medium">
                Context <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <Textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Add context about this recording…"
                rows={3}
              />
            </section>

            <section className="space-y-2">
              <label className="text-sm font-medium">
                Key terms <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <Input
                value={keyTerms}
                onChange={(e) => setKeyTerms(e.target.value)}
                placeholder="Names, brands, technical terms…"
              />
            </section>

            {briefFields.length > 0 ? (
              <section className="space-y-4 rounded-xl border border-border bg-muted/30 p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
                  Role-specific settings
                </h3>
                <DynamicBriefFields
                  fields={briefFields}
                  values={fieldValues}
                  onChange={(key, value) => setFieldValues((prev) => ({ ...prev, [key]: value }))}
                />
              </section>
            ) : null}

            {error ? (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            ) : null}

            <div className="flex items-center justify-between pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                data-qa="new-project-back"
                onClick={() => setStep(1)}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <Button
                className="gap-2"
                disabled={!file || missingRequired}
                data-qa="new-project-upload"
                onClick={() => guardLimit(startUpload)}
              >
                <Upload className="w-4 h-4" />
                Upload &amp; process
              </Button>
            </div>
            {missingRequired ? (
              <p className="text-xs text-muted-foreground text-right">
                Fill in the required role-specific fields to continue.
              </p>
            ) : null}
          </div>
        ) : null}

        {/* STEP 3 — processing */}
        {step === 3 ? (
          <div className="space-y-6">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Processing</p>
              <h2 className="text-xl font-semibold tracking-tight truncate">{file?.name}</h2>
              <p className="text-sm text-muted-foreground">
                {isTranscript
                  ? "Transcript only"
                  : [role?.name, selectedScenario?.name].filter(Boolean).join(" · ")}
              </p>
            </div>

            {error ? (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <AlertCircle className="w-4 h-4 text-destructive" />
                  Upload failed
                </div>
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                  Back
                </Button>
              </div>
            ) : job?.status === "FAILED" ? (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <AlertCircle className="w-4 h-4 text-destructive" />
                  Processing failed
                </div>
                <p className="text-sm text-muted-foreground">
                  Something went wrong while processing this recording.
                </p>
              </div>
            ) : (
              (() => {
                // Phase: upload → transcribing → generating → ready.
                const phase: "upload" | "transcribing" | "generating" | "ready" = !uploadDone
                  ? "upload"
                  : job?.status === "COMPLETED" && (totalReports === 0 || allReportsDone)
                    ? "ready"
                    : job?.step === "PROCESSING_TRANSCRIPT" || job?.status === "COMPLETED"
                      ? "generating"
                      : "transcribing";

                const stepPct = job ? getStepProgress(job.step) : 10;
                const genPct = totalReports > 0 ? 85 + Math.round(reportProgress * 0.15) : 88;
                const percent =
                  phase === "upload"
                    ? uploadProgress
                    : phase === "transcribing"
                      ? stepPct
                      : phase === "generating"
                        ? genPct
                        : 100;

                const HEADERS = {
                  upload: { Icon: Upload, title: "Uploading your recording…", spin: true },
                  transcribing: { Icon: AudioWaveform, title: "Transcribing audio…", spin: true },
                  generating: { Icon: Sparkles, title: "Generating reports…", spin: true },
                  ready: { Icon: CheckCircle2, title: "Your report is ready!", spin: false },
                } as const;
                const head = HEADERS[phase];

                const level = phase === "upload" ? 1 : phase === "transcribing" ? 2 : phase === "generating" ? 3 : 4;
                const stageState = (t: number): StageState =>
                  phase === "ready" ? "done" : level > t ? "done" : level === t ? "active" : "pending";

                return (
                  <>
                    {/* Phase header */}
                    <div className="flex items-center gap-3">
                      <div
                        className={
                          "w-11 h-11 rounded-xl flex items-center justify-center shrink-0 " +
                          (phase === "ready" ? "bg-emerald-500/10" : "bg-muted")
                        }
                      >
                        <head.Icon
                          className={
                            "w-5 h-5 " + (phase === "ready" ? "text-emerald-600" : "text-foreground")
                          }
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold tracking-tight">{head.title}</h3>
                          {head.spin ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : null}
                        </div>
                        {phase !== "ready" ? (
                          <p className="text-sm text-muted-foreground mt-0.5">
                            <FunnyLoader />
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {/* Thick branded progress bar */}
                    <div className="space-y-1.5">
                      <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-foreground transition-all duration-500"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <div className="flex justify-end">
                        <span className="text-xs text-muted-foreground tabular-nums">{percent}%</span>
                      </div>
                    </div>

                    {/* Stage checklist */}
                    <div className="space-y-3 rounded-xl border border-border p-5">
                      <StageRow
                        label="Submitting recording"
                        state={stageState(1)}
                        detail={stageState(1) === "active" ? `${uploadProgress}%` : undefined}
                      />
                      <StageRow
                        label="Transcribing & analyzing"
                        state={stageState(2)}
                        detail={stageState(2) === "active" ? `${stepPct}%` : undefined}
                      />
                      <StageRow
                        label="Generating reports"
                        state={stageState(3)}
                        detail={
                          stageState(3) === "active"
                            ? totalReports > 0
                              ? `${readyReports} of ${totalReports}`
                              : "Starting…"
                            : undefined
                        }
                      />
                      <StageRow label="Quality checks" state={stageState(4)} />
                    </div>

                    {/* Per-report breakdown */}
                    {totalReports > 0 ? (
                      <div className="space-y-2.5 rounded-xl border border-border p-5">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{readyReports} of {totalReports} reports ready</span>
                          <span className="text-xs text-muted-foreground tabular-nums">{reportProgress}%</span>
                        </div>
                        <ul className="space-y-1.5">
                          {(promptReports ?? []).map((r) => {
                            const label = PROMPT_REPORT_LABELS[r.report_type] ?? r.report_type;
                            let badge;
                            if (r.status === "PROCESSED") {
                              badge = <span className="text-xs text-emerald-600 font-medium">Ready</span>;
                            } else if (r.status === "PROCESSING") {
                              badge = (
                                <span className="text-xs text-foreground inline-flex items-center gap-1">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Generating
                                </span>
                              );
                            } else if (r.status === "FAILED") {
                              badge = <span className="text-xs text-destructive">Failed</span>;
                            } else {
                              badge = <span className="text-xs text-muted-foreground/70">Queued</span>;
                            }
                            return (
                              <li key={r.id} className="flex items-center justify-between text-sm">
                                <span className={r.status === "PROCESSED" ? "text-foreground" : "text-muted-foreground"}>
                                  {label}
                                </span>
                                {badge}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}

                    <p className="text-xs text-muted-foreground">
                      You can keep working — we&apos;ll open the report automatically when it&apos;s ready.
                    </p>
                  </>
                );
              })()
            )}
          </div>
        ) : null}
      </div>

      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} limit={usage?.limit} />
    </div>
  );
}

export default NewProjectPage;
